<script>
import { mapState } from 'vuex';
import findIndex from 'lodash/findIndex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ReportModel from '@models/Report';
import DailyAllowanceModel from '@models/DailyAllowance';
import DailyAllowanceModal from '@views/reports/dailyAllowances/edit-daily-allowance';
import DailyAllowanceForm from '@views/reports/dailyAllowances/daily-allowance-form';
import { DateTime } from 'luxon';

export default {
  components: {
    FontAwesomeIcon,
    DailyAllowanceForm,
    DailyAllowanceModal,
  },
  props: {
    collapsed: {
      default: false,
      type: Boolean,
      required: false,
    },
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    // eslint-disable-next-line
    reportId: {
      required: true,
    },
    dailyAllowances: {
      default: () => [],
      type: Array,
    },
    locked: {
      type: Number,
      default: 0,
    },
    completed: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      selectedDailyAllowance: null,
      infoShown: false,
      fields: [
        {
          key: 'id',
          label: this.$t('table_labels.id'),
          sortable: true,
          class: 'w-60',
        },
        {
          key: 'date',
          label: this.$t('table_labels.date'),
          sortable: true,
        },
        {
          key: 'allowance',
          label: this.$t('table_labels.country'),
          sortable: true,
        },
        {
          key: 'allowance_type',
          label: this.$t('table_labels.allowance_type'),
          sortable: true,
        },
        {
          key: 'total_amount',
          label: this.$t('table_labels.total_amount'),
        },
        {
          key: 'description',
          label: this.$t('table_labels.description'),
        },
        {
          key: 'paid',
          label: this.$t('fields.paid'),
          sortable: true,
        },
      ],
    };
  },
  computed: {
    ...mapState({
      allowanceTypes: (state) => state.common.allowanceTypes,
    }),
  },
  mounted() {
    if (!this.locked) {
      this.fields.push({
        key: 'actions',
        label: this.$t('table_labels.actions'),
        sortable: false,
      });
    }
  },
  methods: {
    formatDate(date) {
      return DateTime.fromSQL(date).toFormat('dd.MM.yyyy');
    },
    submit(dailyAllowance) {
      this.$emit('submitted', dailyAllowance);
    },
    update(dailyAllowance) {
      this.selectedDailyAllowance = null;
      const key = findIndex(this.dailyAllowances, { id: dailyAllowance.id });
      this.$emit('updated', dailyAllowance, key);
    },
    async deleteDailyAllowance(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.daily_allowance') }))) {
        this.$wait.start(`dailyAllowance${id}`);
        const key = findIndex(this.dailyAllowances, { id });
        const report = new ReportModel({ id: this.reportId });
        const dailyAllowance = new DailyAllowanceModel({ id });
        await dailyAllowance.for(report).delete();
        this.$emit('deleted', id, key);
        this.$wait.end(`dailyAllowance${id}`);
      }
    },
    async duplicate(item) {
      if (confirm(this.$t('alerts.duplicate_entity', { entity: this.$t('entities.daily_allowance') }))) {
        this.$wait.start('daily-allowance processing');
        const report = new ReportModel({ id: this.reportId });
        const dailyAllowance = new DailyAllowanceModel({
          id: `duplicate/${item.id}`,
        });

        dailyAllowance
          .for(report)
          .save()
          .then((response) => {
            this.$wait.end('daily-allowance processing');
            this.$emit('submitted', response.daily_allowance);
          })
          .catch(() => {
            this.$wait.end('daily-allowance processing');
          });
      }
    },
    showMessage() {
      if (this.collapsed && !this.infoShown) {
        this.$dialog.alert(this.$t('infos.daily_allowance_info'), {
          html: true,
        });
        this.infoShown = true;
      }
    },
    exportPDF() {
      if (this.completed === 0) {
        this.$toasted.error(this.$t('errors.report_status_not_completed'));
      } else {
        this.$wait.start('exporting');
        this.$http
          .get(`reports/export-daily-allowances/${this.reportId}`, { responseType: 'blob' })
          .then((response) => {
            this.$wait.end(`exporting`);
            const headers = response.headers;
            let filename = `Daily Allowances - Report id #${this.reportId}`;

            const match = response.headers['content-disposition'].match(/filename="([^"]+)"/);

            if (match && match.length > 1) {
              filename = match[1];
            }

            const blob = new Blob([response.data], { type: headers['content-type'] });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            link.click();
          })
          .catch(() => this.$wait.end('exporting'));
      }
    },
  },
};
</script>

<template>
  <div>
    <b-collapse id="daily-allowance-collapse" v-model="collapsed" class="mt-2" @show="showMessage">
      <DailyAllowanceForm v-if="!locked && !completed" ref="dailyAllowanceForm" :loaded-id="loadedId" :report-id="reportId" @submitted="submit" />
    </b-collapse>
    <small v-if="dailyAllowances.length" class="table-responsive">
      <br />
      <b-table stacked="lg" striped hover :items="dailyAllowances" :fields="fields" small table-class="no-min-width">
        <template v-slot:cell(date)="row">
          {{ formatDate(row.item.date) }}
        </template>
        <template v-slot:cell(allowance)="row">
          <div>
            {{ row.item.allowance.country.name_de }}
          </div>
        </template>
        <template v-slot:cell(total_amount)="row"> € {{ row.item.total_amount }} </template>
        <template v-slot:cell(allowance_type)="row">
          <div> {{ allowanceTypes[row.item.allowance_type] }} </div>
        </template>
        <template v-slot:cell(paid)="row">
          <FontAwesomeIcon v-if="row.item.paid" class="text-success" :icon="['fas', 'check-circle']" />
          <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <b-btn :disabled="$wait.is('daily-allowance processing')" class="mr-2" size="sm" variant="outline-success" @click="duplicate(row.item)">
              <FontAwesomeIcon :icon="['fas', 'copy']" />
            </b-btn>
            <b-btn class="mr-2" size="sm" variant="outline-primary" @click="selectedDailyAllowance = row.item.id">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn class="mr-2" size="sm" variant="outline-danger" @click="deleteDailyAllowance(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
        </template>
      </b-table>
    </small>
    <div v-if="dailyAllowances.length" :class="[collapsed || dailyAllowances.length > 0 ? 'mt-2' : '', 'text-muted']">
      <small>{{ $t('messages.daily_allowances') }}</small>
    </div>
    <b-btn v-if="dailyAllowances.length" class="mt-3" variant="outline-dark" @click="exportPDF">
      {{ $t('actions.download') }}
    </b-btn>
    <DailyAllowanceModal
      v-if="!locked && !completed"
      v-model="selectedDailyAllowance"
      :loaded-id="selectedDailyAllowance"
      :report-id="reportId"
      @updated="update"
      @hidden="selectedCarUse = null"
    />
  </div>
</template>

<style lang="scss" module>
.statusContainer {
  padding-left: 1rem;
  margin: 0;
}

.statusLoadingIcon {
  margin: 0 auto;
}

.dg-pull-right {
  float: inherit !important;
}

.dg-content-footer {
  text-align: center;
}
</style>
